// Transfer Order Types based on C# entity structure

export enum TransferOrderStatus {
   New = 0,
  Allocated = 1,
  Picking = 2,
  Ready = 3,
  InTransit = 4,
  Received = 5,
  Validating = 6,
  Completed = 7
}

export enum TransferOrderRecordStatus {
  Pending = 0,
  Allocated = 1,
  Picking = 2,
  Picked = 3,
  InTransit = 4,
  Validating = 5,
  Completed = 6,
}

export interface TransferOrderItem {
  barcode: string;
  requestedQuantity: number;
}

export interface TransferOrderRecord {
  id: string;
  barcode: string;
  unitId: string | null;
  requestedQuantity: number;
  allocatedQuantity: number;
  pickedQuantity: number;
  shippedQuantity: number;
  receivedQuantity: number;
  sourceStorageUnitId: string | null;
  sourceStorageUnitName: string | null;
  sourceWarehouseId: string | null;
  sourceWarehouseName: string | null;
  boxRefNumber: string | null;
  status: TransferOrderRecordStatus;
  notes: string | null;
}

export interface TransferOrder {
  Id: string;
  ClientId: string;
  ClientName?: string;
  OrderName?: string;
  Status: TransferOrderStatus;
  Items: TransferOrderItem[];
  Records?: TransferOrderRecord[];
  ItemCount?: number;
  TotalQuantity?: number;
  TotalRequestedQuantity?: number;
  TotalAllocatedQuantity?: number;
  TotalPickedQuantity?: number;
  TotalShippedQuantity?: number;
  TotalReceivedQuantity?: number;
  DeliveryAreaStorageUnitId?: string;
  Notes?: string;
  CreatedAt?: string;
  UpdatedAt?: string;
  AllocatedAt?: string;
  PickedAt?: string;
  ShippedAt?: string;
  ReceivedAt?: string;
  CompletedAt?: string;
}

export interface TransferOrderApiResponse {
  id: string;
  name?: string;
  clientId: string;
  clientName?: string;
  status: TransferOrderStatus;
  itemCount?: number;
  totalQuantity?: number;
  items?: TransferOrderItem[];
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface TransferOrdersApiResponse {
  success: boolean;
  message: string;
  data: TransferOrderApiResponse[];
  token: string | null;
}

export interface TransferOrderCreatePayload {
  clientId: string;
  name?: string;
  items: TransferOrderItem[];
  notes?: string;
}

export interface TransferOrderUpdatePayload {
  status?: TransferOrderStatus;
  items?: TransferOrderItem[];
  notes?: string;
}

export interface AllocationRequest {
  transferOrderId: string;
  useSmartAllocation: boolean;
  manualAllocations?: ManualAllocation[];
}

export interface ManualAllocation {
  recordId: string;
  sourceStorageUnitId: string;
  quantity: number;
  boxRefNumber?: string;
}

export interface AllocationResponse {
  success: boolean;
  message: string;
  transferOrderId: string;
  totalRecordsAllocated: number;
  totalQuantityAllocated: number;
  warnings: string[];
  allocations: AllocationDetail[];
}

export interface AllocationDetail {
  recordId: string;
  barcode: string;
  requestedQuantity: number;
  allocatedQuantity: number;
  sourceStorageUnitId: string;
  sourceStorageUnitName: string;
  boxRefNumber: string | null;
}

export interface PickingRequest {
  transferOrderId: string;
  items: PickingItem[];
  notes?: string;
}

export interface PickingItem {
  barcode: string;
  quantity: number;
}

export interface PickingResponse {
  success: boolean;
  message: string;
  transferOrderId: string;
  operationId: string;
  totalItemsPicked: number;
  totalQuantityPicked: number;
  newOrderStatus: TransferOrderStatus;
  warnings: string[];
}

export interface ShippingRequest {
  transferOrderId: string;
  notes?: string;
  trackingNumber?: string;
  carrier?: string;
  estimatedDelivery?: string;
}

export interface ShippingResponse {
  success: boolean;
  message: string;
  transferOrderId: string;
  operationId: string;
  totalItemsShipped: number;
  totalQuantityShipped: number;
  newOrderStatus: TransferOrderStatus;
}

export interface ReceivingRequest {
  transferOrderId: string;
  items: ReceivingItem[];
  isComplete: boolean;
  notes?: string;
}

export interface ReceivingItem {
  barcode: string;
  receivedQuantity: number;
  condition?: 'Good' | 'Damaged';
  notes?: string;
  damagePhotoUrl?: string;
}

export interface ReceivingResponse {
  success: boolean;
  message: string;
  transferOrderId: string;
  operationId: string;
  newStatus: TransferOrderStatus;
  totalItemsReceived: number;
  totalQuantityReceived: number;
  warnings: string[];
}

export interface ReceivedItemState {
  barcode: string;
  expectedQuantity: number;
  receivedQuantity: number;
  condition: 'Good' | 'Damaged' | 'Pending';
  scannedAt: string | null;
  notes: string;
  status: 'pending' | 'partial' | 'complete';
}

export interface PickedItemState {
  barcode: string;
  allocatedQuantity: number;
  pickedQuantity: number;
  sourceLocation: string;
  boxRefNumber: string | null;
  scannedAt: string | null;
  status: 'pending' | 'partial' | 'complete';
}

export const getTransferOrderStatusLabel = (status: TransferOrderStatus): string => {
  switch (status) {
    case TransferOrderStatus.New:
      return 'New';
    case TransferOrderStatus.Allocated:
      return 'Allocated';
    case TransferOrderStatus.Picking:
      return 'Picking';
    case TransferOrderStatus.Ready:
      return 'Ready';
    case TransferOrderStatus.InTransit:
      return 'In Transit';
    case TransferOrderStatus.Received:
      return 'Received';
    case TransferOrderStatus.Validating:
      return 'Validating';
    case TransferOrderStatus.Completed:
      return 'Completed';
    default:
      return 'Unknown';
  }
};

export const getTransferOrderStatusColor = (status: TransferOrderStatus): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case TransferOrderStatus.New:
      return 'info';
    case TransferOrderStatus.Allocated:
      return 'primary';
    case TransferOrderStatus.Picking:
      return 'warning';
    case TransferOrderStatus.Ready:
      return 'secondary';
    case TransferOrderStatus.InTransit:
      return 'warning';
    case TransferOrderStatus.Received:
      return 'info';
    case TransferOrderStatus.Validating:
      return 'primary';
    case TransferOrderStatus.Completed:
      return 'success';
    default:
      return 'default';
  }
};

export const getRecordStatusLabel = (status: TransferOrderRecordStatus): string => {
  switch (status) {
    case TransferOrderRecordStatus.Pending:
      return 'Pending';
    case TransferOrderRecordStatus.Allocated:
      return 'Allocated';
    case TransferOrderRecordStatus.Picking:
      return 'Picking';
    case TransferOrderRecordStatus.Picked:
      return 'Picked';
    case TransferOrderRecordStatus.InTransit:
      return 'In Transit';
    case TransferOrderRecordStatus.Validating:
      return 'Validating';
    case TransferOrderRecordStatus.Completed:
      return 'Completed';
    default:
      return 'Unknown';
  }
};
